import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, { MabarEvent } from '@/services/communityService';
import Notification from '@/components/ui/Notification';
import showToast from '@/components/ui/Toast';
import { getPaginationItems } from '@/utils/pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

const getEventDateParts = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    const monthsShort = [
      'JAN', 'FEB', 'MAR', 'APR', 'MEI', 'JUN',
      'JUL', 'AGU', 'SEP', 'OKT', 'NOV', 'DES',
    ];
    const daysShort = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
    const monthsFull = [
      'JANUARI', 'FEBRUARI', 'MARET', 'APRIL', 'MEI', 'JUNI',
      'JULI', 'AGUSTUS', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DESEMBER',
    ];

    return {
      monthShort: monthsShort[d.getMonth()],
      dateNum: d.getDate(),
      dayOfWeek: daysShort[d.getDay()],
      year: d.getFullYear(),
      monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      monthHeader: `${monthsFull[d.getMonth()]} ${d.getFullYear()}`,
      timestamp: d.getTime(),
    };
  } catch {
    return {
      monthShort: 'JAN',
      dateNum: 1,
      dayOfWeek: 'Sen',
      year: 2026,
      monthKey: '2026-01',
      monthHeader: 'JANUARI 2026',
      timestamp: 0,
    };
  }
};



const formatDateLong = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    const monthsFull = [
      'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
      'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
    ];
    return `${d.getDate()} ${monthsFull[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

interface MabarManageModalProps {
  event: MabarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  navigate: ReturnType<typeof useNavigate>;
  handleShare: (slug: string) => void;
  onRequestCancelOrDelete: (slug: string, isCanceled: boolean) => void;
  onRequestKabari: (slug: string) => void;
}

const MabarManageModal = ({
  event,
  isOpen,
  onClose,
  navigate,
  handleShare,
  onRequestCancelOrDelete,
  onRequestKabari,
}: MabarManageModalProps) => {
  if (!isOpen || !event) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateParts = getEventDateParts(event.event_date);
  const isPastDate = dateParts.timestamp < today.getTime();

  let effectiveStatus = event.status;
  if (event.status === 'canceled') {
    effectiveStatus = 'canceled';
  } else if (event.status === 'ended' || isPastDate) {
    effectiveStatus = 'ended';
  } else {
    effectiveStatus = 'scheduled';
  }

  const isCanceled = effectiveStatus === 'canceled';
  const isEnded = effectiveStatus === 'ended';
  const showKabari = !isCanceled && !isEnded;

  const totalSlots = event.total_slots || (event.config as Record<string, any>)?.total_slots || 0;
  const filledSlots = event.filled_slots || 0;

  const venueText = `${event.lokasi_arena || event.address || 'Venue'} - ${event.city || 'Indonesia'}`;
  const timeRangeText = formatLocalEventTimeRange(event.start_time, event.end_time);
  const sportName = event.sport_category?.name || 'Olahraga';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator */}
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

        {/* Header Title & Close Button */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h2 className="text-xl font-extrabold text-[#0E1726] leading-snug">
              {event.name}
            </h2>
            <div>
              {isEnded ? (
                <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-bold tracking-wider uppercase">
                  SELESAI
                </span>
              ) : isCanceled ? (
                <span className="inline-block px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs font-bold tracking-wider uppercase">
                  DIBATALKAN
                </span>
              ) : (
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-bold tracking-wider uppercase">
                  TERJADWAL
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {/* Details List */}
        <div className="space-y-4 pt-1">
          {/* Waktu */}
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF5F2] flex items-center justify-center shrink-0">
              <Icon icon="ph:calendar-blank-bold" className="w-5 h-5 text-[#FC480D]" />
            </div>
            <div className="space-y-0.5">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                WAKTU
              </div>
              <div className="text-sm font-bold text-[#0E1726]">
                {formatDateLong(event.event_date)} · {timeRangeText}
              </div>
            </div>
          </div>

          {/* Venue */}
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF5F2] flex items-center justify-center shrink-0">
              <Icon icon="ph:map-pin-bold" className="w-5 h-5 text-[#FC480D]" />
            </div>
            <div className="space-y-0.5">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                VENUE
              </div>
              <div className="text-sm font-bold text-[#0E1726] line-clamp-1">
                {venueText}
              </div>
            </div>
          </div>

          {/* Olahraga */}
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF5F2] flex items-center justify-center shrink-0">
              <Icon icon="ph:soccer-ball-bold" className="w-5 h-5 text-[#FC480D]" />
            </div>
            <div className="space-y-0.5">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                OLAH RAGA
              </div>
              <div className="text-sm font-bold text-[#0E1726]">
                {sportName}
              </div>
            </div>
          </div>

          {/* Peserta */}
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF5F2] flex items-center justify-center shrink-0">
              <Icon icon="ph:users-bold" className="w-5 h-5 text-[#FC480D]" />
            </div>
            <div className="space-y-0.5">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                PESERTA
              </div>
              <div className="text-sm font-bold text-[#0E1726]">
                {filledSlots}/{totalSlots || filledSlots} terisi
              </div>
            </div>
          </div>
        </div>

        {/* Action Grid (4 Cards) */}
        <div className="grid grid-cols-4 gap-2.5 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(`/host/mabar/${event.slug}/dashboard`);
            }}
            className="bg-[#FFF5F2] hover:bg-orange-100 p-3 rounded-2xl text-center flex flex-col items-center justify-center gap-1 transition-all cursor-pointer active:scale-95"
          >
            <Icon icon="ph:chart-bar-bold" className="w-5 h-5 text-[#FC480D]" />
            <span className="text-xs font-bold text-[#FC480D]">Analitik</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              const sportSlug = event.sport_category?.slug?.toLowerCase() || '';
              if (sportSlug === 'badminton') {
                navigate(`/host/mabar/${event.slug}/badminton`);
              } else if (sportSlug === 'padel') {
                navigate(`/host/mabar/${event.slug}/padel`);
              } else if (sportSlug === 'pickleball') {
                navigate(`/host/mabar/${event.slug}/pickleball`);
              } else {
                navigate(`/host/mabar/${event.slug}/statistic`);
              }
            }}
            className="bg-[#FFF5F2] hover:bg-orange-100 p-3 rounded-2xl text-center flex flex-col items-center justify-center gap-1 transition-all cursor-pointer active:scale-95"
          >
            <Icon icon="ph:chart-pie-slice-bold" className="w-5 h-5 text-[#FC480D]" />
            <span className="text-xs font-bold text-[#FC480D]">Statistik</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(`/host/mabar/${event.slug}/edit`);
            }}
            className="bg-[#FFF5F2] hover:bg-orange-100 p-3 rounded-2xl text-center flex flex-col items-center justify-center gap-1 transition-all cursor-pointer active:scale-95"
          >
            <Icon icon="ph:pencil-simple-bold" className="w-5 h-5 text-[#FC480D]" />
            <span className="text-xs font-bold text-[#FC480D]">Edit</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              handleShare(event.slug);
            }}
            className="bg-[#FFF5F2] hover:bg-orange-100 p-3 rounded-2xl text-center flex flex-col items-center justify-center gap-1 transition-all cursor-pointer active:scale-95"
          >
            <Icon icon="ph:share-network-bold" className="w-5 h-5 text-[#FC480D]" />
            <span className="text-xs font-bold text-[#FC480D]">Bagikan</span>
          </button>
        </div>

        {/* Kabari member yang belum join, lalu batalkan / hapus */}
        <div className={`pt-1 ${showKabari ? 'grid grid-cols-2 gap-2' : ''}`}>
          {showKabari && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onRequestKabari(event.slug);
              }}
              className="py-2.5 rounded-xl text-xs font-bold text-[#ffffff] bg-[#FC480D] hover:bg-orange-100 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
            >
              <Icon icon="ph:bell-ringing" className="w-4 h-4 text-[#ffffff]" />
              <span>Kabari Member</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onClose();
              onRequestCancelOrDelete(event.slug, isCanceled);
            }}
            className={`${showKabari ? '' : 'w-full'} py-2.5 rounded-xl text-xs font-bold text-red-500 hover:bg-red-50 flex items-center justify-center gap-1.5 transition-colors cursor-pointer`}
          >
            <Icon
              icon={isCanceled ? 'ph:trash-bold' : 'ph:x-circle-bold'}
              className="w-4 h-4 text-red-500"
            />
            <span>{isCanceled ? 'Hapus Event' : 'Batalkan Event'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Skeleton Component for Event List
function MabarListSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Search Bar Skeleton */}
      <div className="flex gap-2">
        <div className="flex-1 h-11 bg-gray-200 rounded-xl" />
        <div className="w-11 h-11 bg-gray-200 rounded-xl" />
      </div>

      {/* Month Group Skeleton 1 */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center gap-3">
          <div className="h-4 w-28 bg-gray-200 rounded-md" />
          <div className="flex-1 h-[1px] bg-gray-200" />
          <div className="h-4 w-14 bg-gray-200 rounded-md" />
        </div>

        <div className="h-52 bg-white border border-[#E0E6ED] rounded-2xl p-4 space-y-3" />
        <div className="h-52 bg-white border border-[#E0E6ED] rounded-2xl p-4 space-y-3" />
      </div>

      {/* Month Group Skeleton 2 */}
      <div className="space-y-3 pt-4">
        <div className="flex items-center gap-3">
          <div className="h-4 w-36 bg-gray-200 rounded-md" />
          <div className="flex-1 h-[1px] bg-gray-200" />
          <div className="h-4 w-14 bg-gray-200 rounded-md" />
        </div>

        <div className="h-52 bg-white border border-[#E0E6ED] rounded-2xl p-4 space-y-3" />
      </div>
    </div>
  );
}

const MabarListPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<MabarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter & Navigation states
  const [activeTab, setActiveTab] = useState<'upcoming' | 'riwayat'>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedManageEvent, setSelectedManageEvent] = useState<MabarEvent | null>(null);
  const [kabariConfirmation, setKabariConfirmation] = useState({ isOpen: false, slug: '' });
  const [kabariBusy, setKabariBusy] = useState(false);

  // Pagination state (Max 10 events per page)
  const [page, setPage] = useState<number>(1);
  const [totalEvents, setTotalEvents] = useState<number>(0);
  const LIMIT = 10;

  // Dialog & Notifications
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    type?: 'success' | 'error';
  }>({ isOpen: false, message: '', type: 'success' });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    slug: string;
    actionLabel: string;
  }>({
    isOpen: false,
    slug: '',
    actionLabel: '',
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadEvents = useCallback(async (
    targetPage: number,
    tab: 'upcoming' | 'riwayat',
    sport: string,
    search: string
  ) => {
    try {
      setLoading(true);
      const response = await communityService.getMyEvents({
        page: targetPage,
        limit: LIMIT,
        tab,
        sport,
        search,
      });
      setEvents(response.events || []);
      setTotalEvents(response.total ?? (response.events ? response.events.length : 0));
      setPage(targetPage);
    } catch (error) {
      console.error('Error loading events:', error);
      setNotification({
        isOpen: true,
        message: 'Gagal memuat data event',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Whenever activeTab, selectedSport, or debouncedSearch changes -> reset page to 1 & hit API
  useEffect(() => {
    loadEvents(1, activeTab, selectedSport, debouncedSearch);
  }, [activeTab, selectedSport, debouncedSearch, loadEvents]);

  const handleTabChange = (tab: 'upcoming' | 'riwayat') => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const totalPagesCount = Math.max(1, Math.ceil(totalEvents / LIMIT));
    if (newPage < 1 || newPage > totalPagesCount || newPage === page) return;
    loadEvents(newPage, activeTab, selectedSport, debouncedSearch);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = async (slug: string) => {
    try {
      await communityService.cancelEvent(slug);
      setNotification({
        isOpen: true,
        message: 'Event berhasil dibatalkan',
        type: 'success',
      });
      loadEvents(page, activeTab, selectedSport, debouncedSearch);
    } catch (error) {
      console.error('Error cancel event:', error);
      setNotification({
        isOpen: true,
        message: 'Gagal membatalkan event',
        type: 'error',
      });
    }
  };

  const handleDelete = async (slug: string) => {
    try {
      await communityService.deleteEvent(slug);
      setNotification({
        isOpen: true,
        message: 'Event berhasil dihapus',
        type: 'success',
      });
      loadEvents(page, activeTab, selectedSport, debouncedSearch);
    } catch (error) {
      console.error('Error delete event:', error);
      setNotification({
        isOpen: true,
        message: 'Gagal menghapus event',
        type: 'error',
      });
    }
  };

  const handleKabari = async (slug: string) => {
    setKabariBusy(true);
    try {
      const result = await communityService.notifyUnjoinedMembers(slug);
      setNotification({
        isOpen: true,
        message: `Berhasil mengabari ${result.sent} member`,
        type: 'success',
      });
    } catch (error) {
      setNotification({
        isOpen: true,
        message: getApiErrorMessage(error, 'Gagal mengabari member'),
        type: 'error',
      });
    } finally {
      setKabariBusy(false);
      setKabariConfirmation({ isOpen: false, slug: '' });
    }
  };

  const handleShare = (slug: string) => {
    const url = `${window.location.origin}/event/${slug}`;
    navigator.clipboard.writeText(url);
    showToast.success('Link event berhasil disalin ke clipboard');
  };

  // Derive sport categories for filter pills
  const availableSports = useMemo(() => {
    const defaultSports = [
      { slug: 'futsal', name: 'Futsal' },
      { slug: 'mini-soccer', name: 'Mini Soccer' },
      { slug: 'badminton', name: 'Badminton' },
      { slug: 'padel', name: 'Padel' },
      { slug: 'basket', name: 'Basket' },
    ];
    const sportsMap = new Map<string, string>();
    defaultSports.forEach((s) => sportsMap.set(s.slug, s.name));
    events.forEach((ev) => {
      if (ev.sport_category?.name) {
        sportsMap.set(ev.sport_category.slug || ev.sport_category.name.toLowerCase(), ev.sport_category.name);
      }
    });
    return Array.from(sportsMap.entries()).map(([slug, name]) => ({ slug, name }));
  }, [events]);

  // Compute effective status for events (Ended if date passed)
  const processedEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();

    return events.map((ev) => {
      const dateParts = getEventDateParts(ev.event_date);
      const isPastDate = dateParts.timestamp < todayMs;

      let effectiveStatus = ev.status;
      if (ev.status === 'canceled') {
        effectiveStatus = 'canceled';
      } else if (ev.status === 'ended' || isPastDate) {
        effectiveStatus = 'ended';
      } else {
        effectiveStatus = 'scheduled'; // Upcoming / Terjadwal
      }

      return {
        ...ev,
        dateParts,
        effectiveStatus,
        isPastDate,
      };
    });
  }, [events]);

  // Group events by Month (e.g., JULI 2026) and sort
  const groupedEvents = useMemo(() => {
    const groupsMap = new Map<
      string,
      { label: string; yearMonthKey: string; timestamp: number; items: typeof processedEvents }
    >();

    processedEvents.forEach((ev) => {
      const key = ev.dateParts.monthKey;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          label: ev.dateParts.monthHeader,
          yearMonthKey: key,
          timestamp: ev.dateParts.timestamp,
          items: [],
        });
      }
      groupsMap.get(key)!.items.push(ev);
    });

    const groups = Array.from(groupsMap.values());

    // Sort groups and items: Ascending for Upcoming, Descending for Riwayat
    if (activeTab === 'upcoming') {
      groups.sort((a, b) => a.yearMonthKey.localeCompare(b.yearMonthKey));
      groups.forEach((g) => g.items.sort((a, b) => a.dateParts.timestamp - b.dateParts.timestamp));
    } else {
      groups.sort((a, b) => b.yearMonthKey.localeCompare(a.yearMonthKey));
      groups.forEach((g) => g.items.sort((a, b) => b.dateParts.timestamp - a.dateParts.timestamp));
    }

    return groups;
  }, [processedEvents, activeTab]);

  const totalPages = Math.max(1, Math.ceil(totalEvents / LIMIT));

  return (
    <div className="min-h-screen text-[#0E1726]">
      {/* 📱 TAB BAR */}
      <div className="bg-white border-b border-[#E0E6ED] sticky top-0 z-30 shadow-2xs">
        {/* Tab Navigation (Upcoming vs Riwayat) */}
        <div className="max-w-4xl mx-auto flex items-center">
          <button
            type="button"
            onClick={() => handleTabChange('upcoming')}
            className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border-b-2 ${
              activeTab === 'upcoming'
                ? 'border-[#FC480D] text-[#FC480D] bg-orange-50/20'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon icon="ph:calendar-blank-bold" className="w-4 h-4" />
            <span>Upcoming</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('riwayat')}
            className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border-b-2 ${
              activeTab === 'riwayat'
                ? 'border-[#FC480D] text-[#FC480D] bg-orange-50/20'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon icon="ph:clock-counter-clockwise-bold" className="w-4 h-4" />
            <span>Riwayat</span>
          </button>
        </div>
      </div>

      {/* 🔍 SEARCH & FILTER SECTION */}
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4 pb-36 md:pb-8">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {/* Search Input Box */}
            <div className="flex-1 bg-white border border-[#E0E6ED] rounded-xl px-3.5 h-11 flex items-center gap-2 shadow-2xs focus-within:border-[#FC480D] focus-within:ring-1 focus-within:ring-[#FC480D] transition-all">
              <Icon icon="ph:magnifying-glass-bold" className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Cari event atau venue"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent text-sm text-[#0E1726] placeholder-gray-400 outline-none font-medium"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full cursor-pointer"
                >
                  <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className={`h-11 w-11 rounded-xl flex items-center justify-center border transition-all cursor-pointer shrink-0 ${
                filterOpen || selectedSport
                  ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-xs'
                  : 'bg-white text-gray-700 border-[#E0E6ED] hover:bg-gray-50'
              }`}
              aria-label="Toggle Filter"
            >
              <Icon icon="ph:sliders-horizontal-bold" className="w-5 h-5" />
            </button>
          </div>

          {/* Sport Filter Category Pills */}
          {(filterOpen || selectedSport) && (
            <div className="flex items-center gap-2 overflow-x-auto py-1 scrollbar-none transition-all">
              <button
                type="button"
                onClick={() => setSelectedSport('')}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer shrink-0 ${
                  selectedSport === ''
                    ? 'bg-[#FC480D] text-white shadow-xs'
                    : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50'
                }`}
              >
                Semua
              </button>

              {availableSports.map((sport) => (
                <button
                  key={sport.slug}
                  type="button"
                  onClick={() => setSelectedSport(sport.slug)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer shrink-0 ${
                    selectedSport === sport.slug
                      ? 'bg-[#FC480D] text-white shadow-xs'
                      : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50'
                  }`}
                >
                  {sport.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 📋 EVENT LIST CONTENT */}
        {loading ? (
          <MabarListSkeleton />
        ) : groupedEvents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center space-y-3 my-4 shadow-2xs">
            <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto">
              <Icon icon="ph:calendar-blank-bold" className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold text-[#0E1726]">Tidak Ada Event</h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {selectedSport || searchQuery
                  ? 'Tidak ditemukan event yang sesuai dengan filter atau kata kunci pencarian Anda.'
                  : activeTab === 'upcoming'
                    ? 'Belum ada event mendatang yang dijadwalkan. Buat event baru sekarang untuk anggota komunitas!'
                    : 'Belum ada riwayat event yang terselenggara. Mulai buat event komunitas sekarang!'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/host/mabar/create')}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FC480D] hover:bg-[#e03d07] text-white text-xs font-bold rounded-full transition-colors cursor-pointer shadow-xs active:scale-95"
            >
              <Icon icon="ph:plus-bold" className="w-4 h-4" />
              <span>Buat Event</span>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedEvents.map((group) => (
              <div key={group.yearMonthKey} className="space-y-3">
                {/* Month Group Header */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 tracking-wider uppercase shrink-0">
                    {group.label}
                  </span>
                  <div className="flex-1 h-[1px] bg-[#E0E6ED]" />
                  <span className="text-xs font-semibold text-gray-400 shrink-0">
                    {group.items.length} event
                  </span>
                </div>

                {/* Event Cards in Group */}
                <div className="space-y-3">
                  {group.items.map((event) => {
                    const sportName = event.sport_category?.name || 'Mabar';
                    const sportIcon = event.sport_category?.icon || 'ph:soccer-ball-bold';
                    const venueName = event.lokasi_arena || event.address || 'Venue Belum Ditentukan';
                    const hostName = event.host?.name || 'Komunitas';
                    const hostAvatar = event.host?.avatar;

                    const totalSlots = event.total_slots || 0;
                    const filledSlots = event.filled_slots || 0;
                    const remainingSlots = Math.max(totalSlots - filledSlots, 0);

                    const isEnded = event.effectiveStatus === 'ended';
                    const isCanceled = event.effectiveStatus === 'canceled';

                    return (
                      <div
                        key={event.id || event.slug}
                        onClick={() => setSelectedManageEvent(event)}
                        className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden cursor-pointer hover:border-[#FC480D] transition-all active:scale-[0.99] space-y-3 p-4 relative"
                      >
                        {/* Card Header Row: Sport Badge & Status Badge */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 text-[#FC480D] text-xs font-bold">
                            <Icon icon={sportIcon} className="w-3.5 h-3.5" />
                            <span>{sportName}</span>
                          </span>

                          {/* Status Badge */}
                          {isCanceled ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 text-[11px] font-bold">
                              <Icon icon="ph:prohibit-bold" className="w-3.5 h-3.5" />
                              <span>DIBATALKAN</span>
                            </span>
                          ) : isEnded ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 text-[11px] font-bold">
                              <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />
                              <span>SELESAI</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 text-[11px] font-bold">
                              <Icon icon="ph:clock-bold" className="w-3.5 h-3.5" />
                              <span>TERJADWAL</span>
                            </span>
                          )}
                        </div>

                        {/* Main Content Row: Date Box & Details */}
                        <div className="flex gap-3.5 items-start">
                          {/* Date Box */}
                          <div className="w-14 h-16 rounded-xl bg-orange-50/70 border border-orange-100/60 flex flex-col items-center justify-center shrink-0 p-1">
                            <span className="text-[11px] font-bold text-[#FC480D] uppercase leading-none">
                              {event.dateParts.monthShort}
                            </span>
                            <span className="text-2xl font-black text-[#0E1726] leading-tight my-0.5">
                              {event.dateParts.dateNum}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-400 leading-none">
                              {event.dateParts.dayOfWeek}
                            </span>
                          </div>

                          {/* Details Column */}
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <h3 className="text-base font-bold text-[#0E1726] leading-snug line-clamp-1 hover:text-[#FC480D] transition-colors">
                              {event.name}
                            </h3>

                            <div className="space-y-1 text-xs text-gray-500 font-medium">
                              <div className="flex items-center gap-1.5">
                                <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="truncate">
                                  {formatLocalEventTimeRange(event.start_time, event.end_time)}
                                </span>
                              </div>

                              <div className="flex items-center gap-1.5">
                                <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="line-clamp-1">{venueName}</span>
                              </div>

                              <div className="flex items-center gap-1.5 pt-0.5">
                                {hostAvatar ? (
                                  <img
                                    src={hostAvatar}
                                    alt={hostName}
                                    className="w-5 h-5 rounded-full object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="w-5 h-5 rounded-md bg-[#FC480D] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                    GM
                                  </div>
                                )}
                                <span className="font-bold text-[#0E1726] truncate">{hostName}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Card Footer: Slot Info & Action Button */}
                        <div className="pt-2.5 border-t border-[#E0E6ED] flex items-center justify-between gap-2">
                          {/* Footer Left: Slot / Attendance Note */}
                          {isEnded ? (
                            <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                              <Icon icon="ph:check-bold" className="w-4 h-4 text-emerald-500 shrink-0" />
                              <span>{filledSlots} pemain hadir</span>
                            </div>
                          ) : isCanceled ? (
                            <div className="flex items-center gap-1 text-xs font-semibold text-gray-400">
                              <Icon icon="ph:info-bold" className="w-4 h-4 text-gray-400 shrink-0" />
                              <span>Event dibatalkan</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-xs font-semibold text-[#FC480D]">
                              <Icon icon="ph:users-bold" className="w-4 h-4 text-[#FC480D] shrink-0" />
                              <span>
                                {remainingSlots > 0 ? `Sisa ${remainingSlots} slot` : 'Penuh'}
                              </span>
                            </div>
                          )}

                          {/* Footer Right: Kelola Action Button */}
                          <div>
                            <button
                              type="button"
                              onClick={() => setSelectedManageEvent(event)}
                              className="h-8 px-3.5 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-xs font-bold flex items-center gap-1 transition-all cursor-pointer shadow-xs active:scale-95"
                            >
                              <Icon icon="ph:gear-six-bold" className="w-3.5 h-3.5" />
                              <span>Kelola</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* 📄 PAGINATION CONTROLS */}
            {totalPages > 1 && (
              <div className="mt-8 pt-4 border-t border-[#E0E6ED] flex flex-col sm:flex-row items-center justify-between gap-4 pb-6">
                <div className="text-xs text-gray-500 font-medium">
                  Menampilkan <span className="font-bold text-[#0E1726]">{(page - 1) * LIMIT + 1}</span> -{' '}
                  <span className="font-bold text-[#0E1726]">{Math.min(page * LIMIT, totalEvents)}</span> dari{' '}
                  <span className="font-bold text-[#0E1726]">{totalEvents}</span> event
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors ${
                      page <= 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-[#E0E6ED] text-gray-700 hover:bg-gray-50 cursor-pointer shadow-2xs'
                    }`}
                  >
                    <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5" />
                    <span>Sebelumnya</span>
                  </button>

                  {getPaginationItems(page, totalPages).map((item, idx) => {
                    if (item === '...') {
                      return (
                        <span key={`ellipsis-${idx}`} className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0">
                          ...
                        </span>
                      );
                    }
                    const p = item as number;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handlePageChange(p)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                          p === page
                            ? 'bg-[#FC480D] text-white shadow-xs'
                            : 'bg-white border border-[#E0E6ED] text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors ${
                      page >= totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-[#E0E6ED] text-gray-700 hover:bg-gray-50 cursor-pointer shadow-2xs'
                    }`}
                  >
                    <span>Berikutnya</span>
                    <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ➕ FLOATING ACTION BUTTON (FAB) FOR MOBILE */}
      <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-40 md:bottom-8 md:right-8">
        <button
          type="button"
          onClick={() => navigate('/host/mabar/create')}
          className="h-12 px-5 rounded-full bg-gradient-to-r from-[#FF8552] to-[#FC480D] text-white text-sm font-bold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all cursor-pointer active:scale-95"
        >
          <Icon icon="ph:plus-bold" className="w-4 h-4 md:w-5 md:h-5" />
          <span>Tambah Event</span>
        </button>
      </div>

      {/* 🔔 NOTIFICATION TOAST */}
      {notification.isOpen && (
        <Notification
          isOpen={notification.isOpen}
          onClose={() => setNotification({ isOpen: false, message: '', type: 'success' })}
          message={notification.message}
          variant={notification.type}
        />
      )}

      {/* ⚠️ ACTION CONFIRMATION DIALOG */}
      <AlertDialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open) =>
          setDeleteConfirmation((prev) => ({
            ...prev,
            isOpen: open,
          }))
        }
      >
        <AlertDialogContent className="rounded-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-[#0E1726]">
              Konfirmasi Aksi
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-gray-500">
              Apakah Anda yakin ingin {deleteConfirmation.actionLabel} event ini? Aksi ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl text-xs font-semibold">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteConfirmation.actionLabel === 'batalkan') {
                  handleCancel(deleteConfirmation.slug);
                } else {
                  handleDelete(deleteConfirmation.slug);
                }
                setDeleteConfirmation({
                  isOpen: false,
                  slug: '',
                  actionLabel: '',
                });
              }}
              className="bg-[#FC480D] hover:bg-[#e03d07] text-white rounded-xl text-xs font-bold"
            >
              Ya, {deleteConfirmation.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* 📱 KELOLA EVENT BOTTOM MODAL */}
      <AlertDialog
        open={kabariConfirmation.isOpen}
        onOpenChange={(open) => {
          if (!open && !kabariBusy) setKabariConfirmation({ isOpen: false, slug: '' });
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold">Kabari Member</AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-gray-500">
              Kirim notifikasi ke member komunitas yang belum join event ini?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={kabariBusy} className="rounded-xl text-xs font-semibold">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={kabariBusy}
              onClick={(event) => {
                event.preventDefault();
                void handleKabari(kabariConfirmation.slug);
              }}
              className="bg-[#FC480D] hover:bg-[#e03d07] text-white rounded-xl text-xs font-bold"
            >
              {kabariBusy ? 'Mengirim...' : 'Kirim'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MabarManageModal
        event={selectedManageEvent}
        isOpen={!!selectedManageEvent}
        onClose={() => setSelectedManageEvent(null)}
        navigate={navigate}
        handleShare={handleShare}
        onRequestCancelOrDelete={(slug, isCanceled) => {
          setDeleteConfirmation({
            isOpen: true,
            slug,
            actionLabel: isCanceled ? 'hapus' : 'batalkan',
          });
        }}
        onRequestKabari={(slug) => setKabariConfirmation({ isOpen: true, slug })}
      />
    </div>
  );
};

export default MabarListPage;
